'use client';
/**
 * Les MISSIONS D'INVENTAIRE — lot G, 18/08/2026.
 *
 * RQ : « il y a un document pour l'inventaire du stock… rajouter une
 * fonctionnalité pour l'affectation des missions d'inventaire et leur
 * périodicité ». Ce qui manquait n'était pas le comptage — c'était la
 * RÉPARTITION : sans affectation, l'inventaire est l'affaire de tout le monde,
 * donc de personne.
 *
 * ── Ce que l'écran montre en premier ────────────────────────────────────────
 *
 * Ce qui est EN RETARD, puis ce qui vient. Une liste triée par ordre de
 * création aurait obligé à chercher ce qui presse ; une mission d'inventaire
 * n'a d'intérêt que par sa date.
 *
 * ── Qui peut quoi ──────────────────────────────────────────────────────────
 *
 * La RQ affecte et planifie. L'ACCUSÉ, lui, est ouvert à tout agent : c'est la
 * personne affectée qui compte, et lui refuser l'enregistrement de son propre
 * geste viderait la mission de son sens. Le journal porte qui a accusé.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { card, EmptyState, Skeleton, inputStyle, Modale, useConfirmation } from '@/components/ui';
import { BoutonJournal } from '@/components/BoutonJournal';
import { Tableau, ligneOuvrable } from '@/shared/Tableau';
import { fmtDate, aujourdhuiIso } from '@/lib/meta';
import { moteur } from './manuel/Registres';
import { useGed } from '@/lib/store';

import { Bouton } from '@/shared/Bouton';
type Passage = {
  documentId: string; faitLe: string; parLibelle: string | null;
  ecarts: number; detailEcarts: string | null; commentaire: string | null;
  ncRef: string | null; saisiLe: string | null; saisiPar: string | null;
};
type Mission = {
  documentId: string; ref: string | null; libelle: string;
  perimetre: string | null; nature: string | null;
  /** Compte les indispensables du catalogue : s'accuse par l'inventaire des quantités. */
  indispensables: boolean;
  responsableKey: string | null; responsableLibelle: string | null;
  periodiciteMois: number | null;
  debutLe: string | null; prochaineEcheance: string | null; dernierePassage: string | null;
  consigne: string | null; observation: string | null;
  actif: boolean; archive: boolean; archiveMotif: string | null;
  enRetard: boolean; passages: Passage[];
};

const NATURES = [
  { v: 'consommables', l: 'Consommables' }, { v: 'reactifs', l: 'Réactifs' },
  { v: 'tubes', l: 'Tubes et contenants' }, { v: 'materiel', l: 'Matériel' },
  { v: 'autre', l: 'Autre' },
];
/** Les périodicités courantes, en clair. Le champ reste un NOMBRE de mois :
 *  un laboratoire qui compte tous les deux mois doit pouvoir l'écrire. */
const PERIODES = [
  { v: 0, l: 'Ponctuelle (sans suite)' }, { v: 1, l: 'Mensuelle' }, { v: 2, l: 'Tous les 2 mois' },
  { v: 3, l: 'Trimestrielle' }, { v: 6, l: 'Semestrielle' }, { v: 12, l: 'Annuelle' },
];
const libellePeriode = (m: number | null) => (!m
  ? 'ponctuelle'
  : PERIODES.find((p) => p.v === m)?.l.toLowerCase() ?? `tous les ${m} mois`);

const cellule: React.CSSProperties = {
  padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)',
  textAlign: 'left', verticalAlign: 'top', fontSize: 12.5,
};
const enTete: React.CSSProperties = {
  ...cellule, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '.03em', color: 'var(--text-muted)', whiteSpace: 'nowrap',
};

/** L'accusé : la preuve que le comptage a eu lieu. */
function FormAccuser({ mission, onFerme, onFait }: {
  mission: Mission; onFerme: () => void; onFait: (message: string) => void;
}) {
  const aujourdhui = aujourdhuiIso();
  const [faitLe, setFaitLe] = useState(aujourdhui);
  const [ecarts, setEcarts] = useState('0');
  const [detail, setDetail] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const n = Number(ecarts) || 0;

  return (
    <Modale titre={`Accuser — ${mission.libelle}`}
      sousTitre={`${mission.perimetre || 'périmètre non précisé'} · ${libellePeriode(mission.periodiciteMois)}`}
      onClose={onFerme}>
      <div style={{ display: 'grid', gap: 10 }}>
        {mission.consigne && (
          <div style={{ padding: '8px 10px', background: 'var(--cyan-50)', border: '1px solid var(--cyan-200)', borderRadius: 'var(--radius-sm)', fontSize: 12.5 }}>
            <strong>Consigne — </strong>{mission.consigne}
          </div>
        )}
        <label style={{ fontSize: 12.5 }}>
          Fait le<br />
          {/* La date du GESTE, pas celle de la saisie : un comptage du vendredi
              enregistré le lundi reste un comptage du vendredi. */}
          <input type="date" value={faitLe} max={aujourdhui} onChange={(e) => setFaitLe(e.currentTarget.value)} style={inputStyle} />
        </label>
        <label style={{ fontSize: 12.5 }}>
          Écarts constatés<br />
          <input type="number" min={0} value={ecarts} onChange={(e) => setEcarts(e.currentTarget.value)} style={{ ...inputStyle, width: 110 }} />
          <span style={{ marginLeft: 8, color: n === 0 ? 'var(--green-600)' : 'var(--amber-600)' }}>
            {n === 0 ? 'conforme' : `${n} écart(s) — à décrire`}
          </span>
        </label>
        {n > 0 && (
          <label style={{ fontSize: 12.5 }}>
            Lesquels ?<br />
            <textarea value={detail} rows={3} onChange={(e) => setDetail(e.currentTarget.value)}
              placeholder="Ce qu'une revue ira lire : quel article, quel écart, où."
              style={{ ...inputStyle, width: '100%' }} />
          </label>
        )}
        <label style={{ fontSize: 12.5 }}>
          Commentaire (facultatif)<br />
          <textarea value={commentaire} rows={2} onChange={(e) => setCommentaire(e.currentTarget.value)} style={{ ...inputStyle, width: '100%' }} />
        </label>
        {erreur && <div style={{ color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</div>}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Bouton variante="primaire" disabled={envoi || (n > 0 && detail.trim().length < 5)} onClick={() => {
              setEnvoi(true); setErreur('');
              void moteur(`inventaire/${mission.documentId}/accuser`, {
                faitLe, ecarts: n, detailEcarts: detail, commentaire,
              }).then((r) => {
                setEnvoi(false);
                if (!r.ok) { setErreur(r.message); return; }
                const suite = (r.data as { prochaineEcheance?: string }).prochaineEcheance;
                onFait(`Inventaire accusé${suite ? ` — prochaine échéance le ${fmtDate(suite)}.` : ' — mission ponctuelle, sans suite.'}`);
              });
            }}>
            Enregistrer l’accusé
          </Bouton>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Un accusé ne se modifie pas : une erreur se corrige par un nouvel accusé, et le journal garde les deux.
          </span>
        </div>
      </div>
    </Modale>
  );
}

/** Créer ou corriger une mission — réservé à la RQ. */
function FormMission({ mission, zones, onFerme, onFait }: {
  mission: Mission | null; zones: string[]; onFerme: () => void; onFait: (m: string) => void;
}) {
  const [v, setV] = useState({
    libelle: mission?.libelle || '', perimetre: mission?.perimetre || '',
    nature: mission?.nature || 'consommables',
    responsableLibelle: mission?.responsableLibelle || '',
    periodiciteMois: String(mission?.periodiciteMois ?? 3),
    debutLe: mission?.debutLe || aujourdhuiIso(),
    consigne: mission?.consigne || '', observation: mission?.observation || '',
    indispensables: !!mission?.indispensables,
  });
  /* Ce que l'écran a lu, figé à l'ouverture (14/10/2026) : en modification,
     le moteur n'écrit que les champs changés depuis cette lecture. */
  const [base] = useState(() => ({ ...v, periodiciteMois: Number(v.periodiciteMois) }));
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const maj = (k: keyof typeof v, val: string) => setV({ ...v, [k]: val });

  return (
    <Modale titre={mission ? `Mission ${mission.ref || ''}` : 'Nouvelle mission d’inventaire'}
      sousTitre="Qui compte quoi, et à quelle fréquence." onClose={onFerme}>
      <div style={{ display: 'grid', gap: 10 }}>
        <label style={{ fontSize: 12.5 }}>
          Intitulé<br />
          <input value={v.libelle} onChange={(e) => maj('libelle', e.currentTarget.value)}
            placeholder="Ex. Inventaire trimestriel des consommables — zone pré-analytique"
            style={{ ...inputStyle, width: '100%' }} />
        </label>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12.5 }}>
            Périmètre (zone de stockage)<br />
            <input list="zones-stockage" value={v.perimetre} onChange={(e) => maj('perimetre', e.currentTarget.value)}
              style={{ ...inputStyle, width: 240 }} />
            {/* Les zones VIENNENT du registre des lieux, mais le champ reste
                libre : une mission peut couvrir un périmètre qui n'y figure
                pas encore, et l'interdire obligerait à créer un lieu pour
                pouvoir planifier un comptage. */}
            <datalist id="zones-stockage">{zones.map((z) => <option key={z} value={z} />)}</datalist>
          </label>
          <label style={{ fontSize: 12.5 }}>
            Nature<br />
            <select value={v.nature} onChange={(e) => maj('nature', e.currentTarget.value)} style={inputStyle}>
              {NATURES.map((n) => <option key={n.v} value={n.v}>{n.l}</option>)}
            </select>
          </label>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12.5 }}>
            Affectée à<br />
            <input value={v.responsableLibelle} onChange={(e) => maj('responsableLibelle', e.currentTarget.value)}
              placeholder="Nom de la personne" style={{ ...inputStyle, width: 220 }} />
          </label>
          <label style={{ fontSize: 12.5 }}>
            Périodicité<br />
            <select value={v.periodiciteMois} onChange={(e) => maj('periodiciteMois', e.currentTarget.value)} style={inputStyle}>
              {PERIODES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12.5 }}>
            Première échéance<br />
            <input type="date" value={v.debutLe} onChange={(e) => maj('debutLe', e.currentTarget.value)} style={inputStyle} />
          </label>
        </div>
        {/* 20/10/2026 : une mission peut COMPTER les indispensables du
            catalogue. Elle s'accuse alors par la saisie de l'inventaire des
            quantités, qui exige tous les indispensables et calcule les écarts. */}
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={v.indispensables} onChange={(e) => setV({ ...v, indispensables: e.currentTarget.checked })} />
          <span>
            <strong>Inventaire des produits indispensables</strong> — la personne affectée saisit les quantités comptées
            (Matériel › Stock › Quantités & inventaire) ; l’accusé se fait à l’enregistrement, avec les écarts au registre.
            {v.indispensables && v.periodiciteMois !== '1' && <span style={{ display: 'block', color: 'var(--amber-700)' }}>Les indispensables se comptent au moins une fois par mois : choisissez « Mensuelle ».</span>}
          </span>
        </label>
        <label style={{ fontSize: 12.5 }}>
          Consigne — ce qu’il faut vérifier<br />
          <textarea value={v.consigne} rows={3} onChange={(e) => maj('consigne', e.currentTarget.value)}
            placeholder="Lue par la personne affectée au moment de compter."
            style={{ ...inputStyle, width: '100%' }} />
        </label>
        {erreur && <div style={{ color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</div>}
        <div>
          <Bouton variante="primaire" disabled={envoi || !v.libelle.trim()} onClick={() => {
              setEnvoi(true); setErreur('');
              void moteur(mission ? `inventaire/${mission.documentId}` : 'inventaire', {
                ...v, periodiciteMois: Number(v.periodiciteMois), ...(mission ? { base } : {}),
              }).then((r) => {
                setEnvoi(false);
                if (!r.ok) { setErreur(r.message); return; }
                onFait(mission ? 'Mission modifiée.' : 'Mission créée.');
              });
            }}>
            {mission ? 'Enregistrer' : 'Créer la mission'}
          </Bouton>
        </div>
      </div>
    </Modale>
  );
}

export function MissionsInventaire({ isRQ, zones = [] }: { isRQ: boolean; zones?: string[] }) {
  const { demanderTexte, confirmation } = useConfirmation();
  const { go } = useGed();
  const [missions, setMissions] = useState<Mission[] | null>(null);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [version, setVersion] = useState(0);
  const [accuser, setAccuser] = useState<Mission | null>(null);
  const [editer, setEditer] = useState<Mission | 'nouvelle' | null>(null);
  const [voirArchivees, setVoirArchivees] = useState(false);

  useEffect(() => {
    let vivant = true;
    void moteur('inventaire').then((r) => {
      if (!vivant) return;
      if (!r.ok) { setErreur(r.message); setMissions([]); return; }
      setMissions((r.data.missions as Mission[]) || []);
    });
    return () => { vivant = false; };
  }, [version]);

  const recharger = (m?: string) => { if (m) setMessage(m); setMissions(null); setVersion((x) => x + 1); };

  /* En RETARD d'abord, puis par échéance : une mission d'inventaire n'a
     d'intérêt que par sa date, et chercher ce qui presse dans une liste
     alphabétique fait perdre le temps qu'on prétend faire gagner. */
  const visibles = useMemo(() => (missions || [])
    .filter((m) => (voirArchivees ? true : !m.archive))
    .sort((a, b) => (Number(b.enRetard) - Number(a.enRetard))
      || String(a.prochaineEcheance || '9999').localeCompare(String(b.prochaineEcheance || '9999'))),
  [missions, voirArchivees]);
  const enRetard = visibles.filter((m) => m.enRetard).length;
  const archivees = (missions || []).filter((m) => m.archive).length;

  if (!missions) return <Skeleton rows={5} />;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '86ch' }}>
        Une mission dit <strong>qui compte quoi, et à quelle fréquence</strong>. Le registre des lieux dit
        où une chose est rangée ; le registre des quantités (Matériel › Stock) dit combien il y en a. La mission dit qui va vérifier que
        les deux correspondent — et son <strong>accusé</strong> est la preuve que le comptage a eu lieu.
      </p>

      {message && <div style={{ fontSize: 12.5, color: 'var(--green-600)' }}>{message}</div>}
      {erreur && <div role="alert" style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {enRetard > 0 && (
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--red-600)' }}>
            {enRetard} mission(s) en retard
          </span>
        )}
        {archivees > 0 && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={voirArchivees} onChange={(e) => setVoirArchivees(e.currentTarget.checked)} />
            voir les {archivees} archivée{archivees > 1 ? 's' : ''}
          </label>
        )}
        <span style={{ flex: 1 }} />
        {isRQ && (
          <Bouton variante="primaire" onClick={() => setEditer('nouvelle')}>
            Affecter une mission
          </Bouton>
        )}
      </div>

      {!visibles.length ? (
        <EmptyState titre="Aucune mission d’inventaire"
          indice="Le document d'inventaire du stock existe ; ce qui manquait, c'est de dire QUI le tient et à quelle fréquence. Affectez une première mission — une zone, une personne, une périodicité." />
      ) : (
        <div style={{ ...card, padding: 0 }}>
          <Tableau
            defilementHorizontal
            id="missions-inventaire"
            ajusterLargeur
            /* L6b (10/10/2026) : niveau STANDARD ; « Accuser », « Archiver » et le
               journal en colonne d'actions, nommée (É-9). */
            niveau="standard"
            lignes={visibles}
            cleLigne={(m) => m.documentId}
            /* Le clic ouvre la mission ; « Accuser » et « Archiver » restent —
               ce ne sont pas le geste par défaut (Ines, 26/08/2026). */
            ligneProps={(m) => (isRQ && !m.archive
              ? ligneOuvrable(() => setEditer(m), `Modifier ${m.ref || m.libelle}`, { opacity: m.archive ? 0.55 : 1 })
              : { style: { opacity: m.archive ? 0.55 : 1 } })}
            colonnes={[
              {
                cle: 'ref', libelle: 'Réf.', valeur: (m) => m.ref || '',
                style: { fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' },
                rendu: (m) => m.ref || '—',
              },
              {
                cle: 'libelle', libelle: 'Mission', valeur: (m) => m.libelle,
                style: { fontWeight: 600 },
                rendu: (m) => (
                  <>
                    {m.libelle}
                    {m.indispensables && <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>produits indispensables · quantités</div>}
                    {m.archive && <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>archivée — {m.archiveMotif}</div>}
                    {!m.actif && !m.archive && <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-700)' }}>suspendue</div>}
                  </>
                ),
              },
              { cle: 'perimetre', libelle: 'Périmètre', valeur: (m) => m.perimetre || '', type: 'liste', rendu: (m) => m.perimetre || '—' },
              {
                cle: 'responsable', libelle: 'Affectée à', type: 'liste',
                // Les missions SANS responsable remontent en premier : c'est
                // exactement ce qu'on cherche en triant cette colonne.
                valeur: (m) => m.responsableLibelle || '',
                rendu: (m) => (m.responsableLibelle
                  || <span style={{ color: 'var(--amber-700)' }}>personne — à affecter</span>),
              },
              {
                cle: 'periodicite', libelle: 'Fréquence',
                // Le NOMBRE DE MOIS, pas son libellé : « trimestrielle » et
                // « mensuelle » se classeraient par leur première lettre.
                valeur: (m) => m.periodiciteMois ?? 9999,
                rendu: (m) => libellePeriode(m.periodiciteMois),
              },
              {
                cle: 'prochaine', libelle: 'Prochaine', type: 'date',
                // La date brute : les retards remontent en tête d'un tri
                // croissant, ce qui est la lecture utile de ce registre.
                valeur: (m) => m.prochaineEcheance || '9999-12-31',
                rendu: (m) => (
                  <span style={{ whiteSpace: 'nowrap', color: m.enRetard ? 'var(--red-600)' : undefined, fontWeight: m.enRetard ? 700 : 400 }}>
                    {m.prochaineEcheance ? fmtDate(m.prochaineEcheance) : '—'}
                    {m.enRetard && ' ⚠'}
                  </span>
                ),
              },
              {
                cle: 'dernier', libelle: 'Dernier comptage',
                // Jamais compté = chaîne vide, donc en tête d'un tri croissant :
                // les missions que personne n'a jamais tenues sont celles qu'on
                // cherche.
                valeur: (m) => m.passages[0]?.faitLe || '',
                rendu: (m) => {
                  const dernier = m.passages[0];
                  if (!dernier) return <span style={{ color: 'var(--text-muted)' }}>jamais compté</span>;
                  return (
                    <>
                      {fmtDate(dernier.faitLe)}
                      <div style={{ fontSize: 12.5, color: dernier.ecarts ? 'var(--amber-600)' : 'var(--green-600)' }}>
                        {dernier.ecarts ? `${dernier.ecarts} écart(s)` : 'conforme'}
                        {dernier.parLibelle ? ` · ${dernier.parLibelle}` : ''}
                      </div>
                    </>
                  );
                },
              },
            ]}
            actions={(m) => (
                  <>
                    {!m.archive && (m.indispensables
                      /* Une mission « indispensables » s'accuse en saisissant les
                         quantités : un accusé sans comptage ne prouverait rien. */
                      ? <Bouton variante="primaire" taille="sm" title="Saisir les quantités comptées — l’accusé se fait à l’enregistrement" onClick={() => go('fourn')}>Compter</Bouton>
                      : <Bouton variante="primaire" taille="sm" onClick={() => setAccuser(m)}>Accuser</Bouton>
                    )}
                    {isRQ && !m.archive && (
                      /* Archiver est RÉVERSIBLE : contour, jamais danger (UI-CONVENTIONS §4). */
                      <Bouton taille="sm" onClick={async () => {
                          const motif = await demanderTexte(
                            `Archiver « ${m.libelle} » ?\nSes ${m.passages.length} accusé(s) restent — ce sont des preuves.\n\nMotif :`,
                            { titre: 'Archiver la mission', confirmer: 'Archiver', min: 5 },
                          );
                          if (!motif) return;
                          void moteur(`inventaire/${m.documentId}/archiver`, { motif }).then((r) => {
                            if (!r.ok) { setErreur(r.message); return; }
                            recharger('Mission archivée — ses accusés restent consultables.');
                          });
                        }}>Archiver</Bouton>
                    )}
                    {m.ref && <BoutonJournal reference={m.ref} quoi="de cette mission" />}
                  </>
            )}
          />
        </div>
      )}

      {accuser && (
        <FormAccuser mission={accuser} onFerme={() => setAccuser(null)}
          onFait={(msg) => { setAccuser(null); recharger(msg); }} />
      )}
      {editer && (
        <FormMission mission={editer === 'nouvelle' ? null : editer} zones={zones}
          onFerme={() => setEditer(null)}
          onFait={(msg) => { setEditer(null); recharger(msg); }} />
      )}
      {confirmation}
    </div>
  );
}
